import React from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Add,
  EditOutlined,
  FiberManualRecord,
  LocationOnOutlined,
} from "@mui/icons-material";

import { useCountryDetailContext } from "contexts/CountryDetailContext";

import { FormDrawer } from "components/Shared/FormDrawer";
import { HoverablePaper } from "components/Shared/HoverablePaper";
import { EmptyResult } from "components/Shared/Lottie/EmptyResult";
import { AdministrativeRegionForm } from "./AdministrativeRegionForm";

import {
  useAddCountryRegionMutation,
  useUpdateCountryRegionMutation,
} from "store/services/countryApi";

import { BASE_CONSTANTS } from "constants/baseConstants";

export const CountryAdministrativeRegionsTab = () => {
  const [showForm, setShowForm] = React.useState(false);

  const [selectedRegion, setSelectedRegion] = React.useState(null);

  const { countryDetail } = useCountryDetailContext();

  // RTK Query
  const [addCountryRegion, { isLoading: addingRegion }] =
    useAddCountryRegionMutation();

  const [updateCountryRegion, { isLoading: updatingRegion }] =
    useUpdateCountryRegionMutation();

  // methods
  const handleClose = () => {
    setShowForm(false);
    setSelectedRegion(null);
  };

  const handleAddRegion = async (data) => {
    try {
      await addCountryRegion({
        ...data,
        _id: countryDetail?._id,
      }).unwrap();

      toast.success("Region successfully added!");
    } catch (err) {
      toast.error(err?.data?.message ?? err?.data ?? "Couldn't add region!");
    }
  };

  const handleUpdateRegion = async (data) => {
    try {
      await updateCountryRegion({
        ...data,
        _id: countryDetail?._id,
        regionId: data?._id,
      }).unwrap();

      toast.success("Region successfully added!");
    } catch (err) {
      toast.error(err?.data?.message ?? err?.data ?? "Couldn't update region!");
    }
  };

  return (
    <>
      <Box
        padding={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="inline-flex" alignItems="center" gap={1.5}>
          <LocationOnOutlined color="action" />

          <Typography style={{ fontWeight: 500 }}>
            Administrative Regions
          </Typography>
        </Box>

        <Button
          startIcon={<Add />}
          variant="outlined"
          onClick={() => setShowForm(true)}
        >
          Region
        </Button>
      </Box>

      <Divider />

      {countryDetail?.adminRegions1?.length ? (
        countryDetail?.adminRegions1?.map((region, index) => (
          <React.Fragment key={region?._id}>
            <HoverablePaper>
              <Box
                padding={BASE_CONSTANTS.SPACING_MD}
                display="flex"
                flexDirection="column"
                gap={BASE_CONSTANTS.SPACING_SM}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={BASE_CONSTANTS.SPACING_SM}
                >
                  <div className="font-medium">{region?.ar1Name}</div>

                  <FiberManualRecord style={{ color: "gray", fontSize: 10 }} />

                  <div>{region?.ar1Code}</div>
                </Box>

                <div className="flex flex-wrap items-center gap-4">
                  {region?.adminRegions2?.length
                    ? region?.adminRegions2?.map((subRegion) => (
                        <Chip
                          key={subRegion?._id}
                          label={`${subRegion?.ar2Name} (${subRegion?.ar2Code})`}
                          size="small"
                        />
                      ))
                    : "No sub regions."}
                </div>
              </Box>

              <HoverablePaper.Actions disableAnimation>
                <IconButton
                  onClick={() => {
                    setShowForm(true);
                    setSelectedRegion(region);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </HoverablePaper.Actions>
            </HoverablePaper>

            {index !== countryDetail?.adminRegions1?.length && (
              <Box paddingX={BASE_CONSTANTS.SPACING_MD}>
                <Divider />
              </Box>
            )}
          </React.Fragment>
        ))
      ) : (
        <EmptyResult height={150} />
      )}

      <FormDrawer
        title="Administrative Region"
        open={showForm}
        onClose={handleClose}
      >
        <AdministrativeRegionForm
          isBusy={addingRegion || updatingRegion}
          region={selectedRegion}
          onSubmit={(data) =>
            selectedRegion ? handleUpdateRegion(data) : handleAddRegion(data)
          }
          onCancel={handleClose}
        />
      </FormDrawer>
    </>
  );
};
